import React, { useMemo, useEffect, useState } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Card,
    Box,
    Button,
    Grid,
    CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useFormik } from 'formik';
import _ from 'lodash';
import { useNavigate } from 'react-router';
import { BsFillTriangleFill } from 'react-icons/bs';
import useApi from 'src/api/api';
import { useBackDrop } from 'src/provider/BackdropProvider';
import { useLocale } from 'src/provider/LocaleProvider';
import { useSnack } from 'src/provider/SnackbarProvider';
import LayoutContainer from 'src/layout/LayoutContainer';
import EditDialog from 'src/components/EditDialog';
import utils from 'src/utils/utils';
import classNames from 'classnames';

const yup = require('yup');

// Normalize string for comparisons: handle null/undefined, trim whitespace and normalize unicode
const normalizeStr = (s) => {
    const raw = s === undefined || s === null ? '' : s.toString();
    const trimmed = raw.trim();
    const normalized = typeof trimmed.normalize === 'function' ? trimmed.normalize('NFC') : trimmed;
    
    // Handle percentage values: "11.30%" should equal "11.3%"
    // Also handle decimal numbers: "1.50" should equal "1.5"
    const percentMatch = normalized.match(/^(-?\d+\.?\d*)%$/);
    if (percentMatch) {
        // Parse the number, then add % back - this removes trailing zeros
        const num = parseFloat(percentMatch[1]);
        return isNaN(num) ? normalized : `${num}%`;
    }
    
    // Handle plain decimal numbers: "1.50" should equal "1.5"
    const numberMatch = normalized.match(/^-?\d+\.\d+$/);
    if (numberMatch) {
        const num = parseFloat(normalized);
        return isNaN(num) ? normalized : num.toString();
    }
    
    return normalized;
};

// Helper function to format dates for consistent handling
const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return '';
    
    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
    }
    
    // Handle ISO date format
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateValue)) {
        try {
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
                return date.toISOString().slice(0, 10);
            }
        } catch (error) {
            return dateValue; 
        }
    }
    
    // Handle DD/MM/YYYY format 
    if (typeof dateValue === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateValue)) {
        const parts = dateValue.split('/');
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        
        // Convert DD/MM/YYYY to YYYY-MM-DD
        return `${year}-${month}-${day}`;
    }

    return dateValue;
};

// Helper function to format dates for API submission
const formatDateForAPI = (dateValue) => {
    if (!dateValue) return '';
    
    // If it's already in YYYY-MM-DD format, return as is (API should handle this)
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
    }
    
    // Handle DD/MM/YYYY format
    if (typeof dateValue === 'string' && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateValue)) {
        const parts = dateValue.split('/');
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        
        // Convert DD/MM/YYYY to YYYY-MM-DD
        return `${year}-${month}-${day}`;
    }
    
    // If it's a date string or Date object, convert to YYYY-MM-DD
    try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return '';
        return date.toISOString().slice(0, 10);
    } catch (error) {
        return '';
    }
};

const useStyles = makeStyles({
    table: {
        "& .MuiTableRow-root": {
            height: '50px'
        },
        "& .MuiTableCell-root": {
            whiteSpace: 'nowrap',
            textAlign: 'center',
            border: '1px solid rgba(224, 224, 224, 1)',
            padding: '8px 4px', // Reduce padding for better mobile view
            fontSize: '0.875rem', // Slightly smaller font
            '&:first-child': {
                paddingLeft: '8px'
            },
            '&:last-child': {
                paddingRight: '8px'
            }
        },
        '& .borderLeft': {
            borderLeft: "2px solid rgba(224, 224, 224, 0.8)",
        },
        "& .MuiTableSortLabel-icon": {
            opacity: 0.5,
        },
        "& .Mui-active .MuiTableSortLabel-icon": {
            opacity: 1,
            color: 'limegreen',
        },
        '& .row-existing': {
            backgroundColor: 'rgba(255, 255, 255, 1)', // White background for existing data
            '&:hover': {
                backgroundColor: 'rgba(245, 245, 245, 1)' // Light gray on hover
            }
        },
        '& .row-new': {
            backgroundColor: 'rgba(50, 255, 50,0.2)',
            '&:hover': {
                backgroundColor: 'rgba(50, 255, 50,0.3)'
            }
        },
        '& .row-update': {
            backgroundColor: 'rgba(255, 255, 50,0.2)',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 50,0.3)'
            }
        },
        '& .cell-update': {
            backgroundColor: 'rgba(255, 193, 7, 0.7) !important', 
            fontWeight: 'bold',
            border: '3px solid rgba(255, 165, 0, 1) !important', 
            position: 'relative',
            zIndex: 10 
        },
        '& .error': {
            backgroundColor: 'rgba(255, 50, 50,0.2) !important',
            '&:hover': {
                backgroundColor: 'rgba(255, 50, 50,0.3) !important'
            }
        }
    },
    toolbar: {
        display: 'flex',
        '& .left': {
            display: 'flex',
            margin: '-8px',
            width: 'calc(100% - 16px)',
            flexWrap: 'wrap',
            '& .item': {
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                whiteSpace: 'nowrap'
            }
        },
        '& .right': {
            display: 'flex',
            margin: '-8px',
            width: 'calc(100% - 16px)',
            flexWrap: 'wrap',
            '& .item': {
                display: 'flex',
                alignItems: 'center',
                padding: '8px',
                whiteSpace: 'nowrap'
            }
        }
    }
});

const GameUploadTable = ({
    existingGames = [],
    rows = [],
    loading = false,
    _orderBy = 'index',
    _order = 'asc',
    rowsPerPage = 10,
    rowsPerPageOptions = [5, 10, 25],
    rowsRef,
    formik,
    ...rest
}) => {

    const { t } = useLocale();
    const classes = useStyles();

    const [order, setOrder] = useState(_order);
    const [orderBy, setOrderBy] = useState(_orderBy);

    const sorted = _.orderBy(_.map(rows, (r, index) => ({ ...r, index: index + 1 })), [orderBy], [order]);

    const handleSort = (path) => {
        if (orderBy === path) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        }
        else {
            setOrderBy(path);
            setOrder('asc');
        }
    };

    const headCell = (label, path, sort = true) => {
        if (sort) {
            return (
                <TableSortLabel active={orderBy === path} direction={orderBy === path ? order : 'asc'} onClick={(e) => handleSort(path)} hideSortIcon={false} >
                    {label}
                </TableSortLabel>
            );
        }
        else return label;
    };

    return (
        <Card {...rest}>
            <Box sx={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '500px' }}>
                <Table size='small' className={classNames(classes.table)} sx={{ minWidth: 1400 }}>
                    <TableHead>
                        <TableRow sx={{ height: '30px' }}  >
                            <TableCell width='60px' sx={{ minWidth: '60px' }}>{headCell(t('index'), 'index')}</TableCell>
                            <TableCell sx={{ minWidth: '80px' }}>{headCell(t('order'), 'order')}</TableCell>
                            <TableCell width='80px' sx={{ minWidth: '80px' }}>{headCell(t('game_id'), 'id', false)}</TableCell>
                            <TableCell className='borderLeft' sx={{ minWidth: '120px' }}>{headCell(t('name'), 'name')}</TableCell>
                            <TableCell sx={{ minWidth: '100px' }}>{headCell(t('description'), 'description')}</TableCell>
                            <TableCell sx={{ minWidth: '80px' }}>{headCell(t('type'), 'gameType')}</TableCell>
                            <TableCell sx={{ minWidth: '100px' }}>{headCell(t('feature_start'), 'featureStart')}</TableCell>
                            <TableCell sx={{ minWidth: '100px' }}>{headCell(t('feature_end'), 'featureEnd')}</TableCell>
                            <TableCell sx={{ minWidth: '90px' }}>{headCell(t('point_correct'), 'pointCorrect')}</TableCell>
                            <TableCell sx={{ minWidth: '90px' }}>{headCell(t('point_incorrect'), 'pointIncorrect')}</TableCell>
                            <TableCell sx={{ minWidth: '100px' }}>{headCell(t('point_exchange_rate'), 'pointExchangeRate')}</TableCell>
                            <TableCell sx={{ minWidth: '80px' }}>{headCell(t('status'), 'isActive')}</TableCell>
                            <TableCell sx={{ minWidth: '100px' }}>{headCell(t('filters'), 'filters_name')}</TableCell>
                            <TableCell sx={{ minWidth: '100px' }}>{headCell(t('subjects'), 'subjects_name')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={14} align="center" style={{ padding: '40px' }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : _.map(sorted, (row, index) => {
                            var errors = _.get(formik.errors, row.index - 1);
                            var isError = _.size(errors) > 0;
                            
                            // Use the status field to determine row styling
                            const rowClasses = {
                                'row-existing': row.status === 'existing' && !isError,
                                'row-new': row.status === 'new' && !isError,
                                'row-update': false, // Don't color entire row for updates, only individual cells
                                'error': isError
                            };
                            
                            // Find existing game for field comparison
                            var existingGame = row.id 
                                ? _.find(existingGames, game => game._id === row.id || game.id === row.id)
                                : _.find(existingGames, game => normalizeStr(game.name) === normalizeStr(row.name));


                            return <>
                                <TableRow style={{ height: 50 }} className={classNames(rowClasses)}>
                                    <TableCell >
                                        {row.index}
                                    </TableCell>
                                    <TableCell className={classNames({ 
                                        'cell-update': existingGame && row.status === 'update' && 
                                            (existingGame.order || 0) !== (row.order || 0)
                                    })}>
                                        {row.order || 0}
                                    </TableCell>
                                    <TableCell >
                                        {row.id || '-'}
                                    </TableCell>
                                    <TableCell className={classNames({ 
                                        'cell-update': existingGame && row.status === 'update' && 
                                            normalizeStr(existingGame.name) !== normalizeStr(row.name)
                                    })}>
                                        {row.name || '-'}
                                    </TableCell>
                                    
                                    {_.map(['description', 'gameType', 'featureStart', 'featureEnd', 'pointCorrect', 'pointIncorrect', 'pointExchangeRate', 'isActive', 'filters_name', 'subjects_name'], (path, cellIndex) => {
                                        var newValue = _.get(row, path);
                                        var oldValue;
                                        
                                        // Helper for number comparison
                                        const normalizeNum = (v) => {
                                            if (v === undefined || v === null || v === '') return 0;
                                            const n = Number(v);
                                            return isNaN(n) ? 0 : n;
                                        };

                                        // Get old value based on field type
                                        if (existingGame) {
                                            if (path === 'description') {
                                                oldValue = normalizeStr(existingGame.description);
                                                newValue = normalizeStr(newValue);
                                            } else if (path === 'gameType') {
                                                oldValue = normalizeStr(existingGame.gameType);
                                                newValue = normalizeStr(newValue);
                                            } else if (path === 'featureStart' || path === 'featureEnd') {
                                                oldValue = normalizeStr(formatDateForDisplay(existingGame[path]));
                                                newValue = normalizeStr(formatDateForDisplay(newValue));
                                            } else if (path === 'pointCorrect') {
                                                oldValue = normalizeNum(existingGame.pointCorrect);
                                                newValue = normalizeNum(newValue);
                                            } else if (path === 'pointIncorrect') {
                                                oldValue = normalizeNum(existingGame.pointIncorrect);
                                                newValue = normalizeNum(newValue);
                                            } else if (path === 'pointExchangeRate') {
                                                oldValue = normalizeNum(existingGame.pointExchangeRate);
                                                newValue = normalizeNum(newValue);
                                            } else if (path === 'isActive') {
                                                oldValue = existingGame.isActive;
                                            } else if (path === 'filters_name') {
                                                oldValue = existingGame.filters?.map(f => 
                                                    f.name?.en || f.name?.zh || f.value || f._id
                                                ).join(', ') || '';
                                                oldValue = normalizeStr(oldValue);
                                                newValue = normalizeStr(newValue);
                                            } else if (path === 'subjects_name') {
                                                oldValue = existingGame.subjects?.map(s => 
                                                    s.name?.en || s.name?.zh || s.name || s._id
                                                ).join(', ') || '';
                                                oldValue = normalizeStr(oldValue);
                                                newValue = normalizeStr(newValue);
                                            }
                                        }
                                        // Only show as changed if it's an update (not a new game)
                                        var isChanged = existingGame && row.status === 'update' && newValue !== oldValue;
                                        var displayValue = newValue;

                                        // For filters_name and subjects_name, use the original value for display
                                        if (path === 'filters_name' || path === 'subjects_name') {
                                            displayValue = _.get(row, path) || '';
                                        }
                                        
                                        // Format display value for isActive - only convert valid boolean values
                                        if (path === 'isActive') {
                                            if (newValue === true) {
                                                displayValue = 'Active';
                                            } else if (newValue === false) {
                                                displayValue = 'Inactive';
                                            } else {
                                                // Invalid value - show as-is for error display
                                                displayValue = String(newValue);
                                            }
                                        }
                                        return <TableCell key={cellIndex} className={classNames({ 'cell-update': isChanged, 'borderLeft': cellIndex === 0 })}>
                                            {displayValue !== undefined && displayValue !== null && displayValue !== '' ? displayValue : '-'}
                                        </TableCell>;
                                    })}
                                </TableRow>
                                {_.size(errors) > 0 && <TableRow>
                                    <TableCell colSpan={99} sx={{ px: 1 }} >
                                        <Box display='flex' flexDirection='row'>
                                            <BsFillTriangleFill style={{ height: '16px', width: '16px', color: 'red' }} />
                                            <Box sx={{ color: 'red', ml: 1, textAlign: 'left' }}>
                                                {_.keys(errors).map(e => {
                                                    return <p key={e} style={{ marginTop: 0, marginBottom: 4 }}>{`${t(e)} : ${errors[e]}`}</p>;
                                                })}
                                            </Box>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                                }
                            </>;
                        })}
                        {!loading && _.size(rows) === 0 &&
                            <TableRow style={{ height: 50 }}>
                                <TableCell colSpan={99}>
                                    <Typography variant='h5'>{t('no_records')}</Typography>
                                </TableCell>
                            </TableRow>
                        }
                    </TableBody>
                </Table>
            </Box>
        </Card >
    );
};

const GameUpload = () => {
    const api = useApi();
    const nav = useNavigate();
    const { t } = useLocale();
    const backDrop = useBackDrop();
    const snack = useSnack();
    
    const [existingGames, setExistingGames] = useState([]);
    const [newGameList, setNewGameList] = useState([]);
    const [availableFilters, setAvailableFilters] = useState([]);
    const [availableSubjects, setAvailableSubjects] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [hasUploadedFile, setHasUploadedFile] = useState(false);
    const [isLoadingSourceData, setIsLoadingSourceData] = useState(true);
    const [sourceDataError, setSourceDataError] = useState(null);
    const [pendingCsvFile, setPendingCsvFile] = useState(null);

    useEffect(() => {
        setIsLoadingSourceData(true);
        setSourceDataError(null);
        Promise.all([
            api.getGameList(),
            api.getOrganizationFilters(),
            api.getSubjects()
        ]).then(([gamesRes, filtersRes, subjectsRes]) => {
            const existingData = gamesRes.data || [];
            setExistingGames(existingData);
            setAvailableFilters(filtersRes.data || []);
            setAvailableSubjects(subjectsRes.data.subjects || []);
            
            
            console.log('Loaded existing games:', existingData.length);
            console.log('Available filters:', filtersRes.data || []);
            console.log('Available subjects:', subjectsRes.data.subjects || []);
            setIsLoadingSourceData(false);
        }).catch((error) => {
            console.error('Error loading source data:', error);
            setSourceDataError(error.message || 'Failed to load source data');
            setIsLoadingSourceData(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Process pending CSV file when source data becomes ready
    useEffect(() => {
        if (!isLoadingSourceData && pendingCsvFile && !sourceDataError) {
            console.log('Source data ready, processing pending CSV file...');
            const processCsv = async () => {
                const file = pendingCsvFile;
                setPendingCsvFile(null);
                
                // Re-process the CSV file now that source data is available
                try {
                    await handleCsv(file);
                } catch (error) {
                    console.error('Error processing pending CSV:', error);
                    setIsLoadingData(false);
                }
            };
            
            processCsv();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoadingSourceData, pendingCsvFile, sourceDataError]);

    // Helper function to validate filters/subjects names against available options
    const validateFilterSubjectNames = (namesString, availableItems, fieldType) => {
        if (!namesString || !namesString.trim()) {
            return { valid: true, invalidNames: [] };
        }
        
        const names = namesString.split(',').map((name) => name.trim()).filter(Boolean);
        const invalidNames = names.filter((name) => {
            const found = availableItems.find(
                (item) =>
                    item.name?.en === name ||
                    item.name?.zh === name ||
                    item.name === name
            );
            return !found;
        });
        
        return {
            valid: invalidNames.length === 0,
            invalidNames: invalidNames
        };
    };

    const schema = yup.array().of(
        yup.object().shape({
            id: yup.string().nullable(), // ID is optional for new games
            name: yup.string().required(t('game_name_required')),
            description: yup.string().nullable(),
            gameType: yup.string().oneOf(['mc', 'interactive']).required(t('game_type_required')),
            order: yup.number()
                .typeError(t('yup_number_type_error'))
                .required(t('yup_number_required'))
                .min(0, t('yup_number_min', { min: 0 }))
                .integer(t('yup_number_type_error')),
            // Feature start/end validation - empty is OK, but invalid format should error
            featureStart: yup.string()
                .nullable()
                .test('valid-date', t('invalid_date_format'), function(value) {
                    if (!value || value === '') return true; // Empty is valid
                    // Check if it's a valid date in YYYY-MM-DD format
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
                    const date = new Date(value);
                    return !isNaN(date.getTime());
                }),
            featureEnd: yup.string()
                .nullable()
                .test('valid-date', t('invalid_date_format'), function(value) {
                    if (!value || value === '') return true; // Empty is valid
                    // Check if it's a valid date in YYYY-MM-DD format
                    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
                    const date = new Date(value);
                    return !isNaN(date.getTime());
                }),
            pointCorrect: yup.number().min(0).nullable(),
            pointIncorrect: yup.number().min(0).nullable(),
            pointExchangeRate: yup.number().min(0).nullable(),
            isActive: yup.mixed()
                .test('valid-status', t('invalid_status_format'), function(value) {
                    // Accept true, false, undefined, or null as valid
                    return value === true || value === false || value === undefined || value === null;
                }),
            filters_name: yup.string().nullable(),
            subjects_name: yup.string().nullable(),
            status: yup.string().oneOf(['existing', 'new', 'update', 'error']).nullable()
        })
        .test('filters-valid', t('invalid_filters'), function (value) {
            const validation = validateFilterSubjectNames(value.filters_name, availableFilters, 'filter');
            if (!validation.valid) {
                return this.createError({
                    path: this.path + '.filters_name',
                    message: t('invalid_filters') + ': ' + validation.invalidNames.join(', ')
                });
            }
            return true;
        })
        .test('subjects-valid', t('invalid_subjects'), function (value) {
            const validation = validateFilterSubjectNames(value.subjects_name, availableSubjects, 'subject');
            if (!validation.valid) {
                return this.createError({
                    path: this.path + '.subjects_name',
                    message: t('invalid_subjects') + ': ' + validation.invalidNames.join(', ')
                });
            }
            return true;
        })
    );

    const formik = useFormik({
        initialValues: newGameList || [],
        enableReinitialize: true,
        validationSchema: schema,
        validate: () => {}, // Enable validation on change
        validateOnChange: true, // Validate on every change
        validateOnBlur: true, // Validate on blur
        onSubmit: async (values, helper) => {
            backDrop.open();
            try {
                let successCount = 0;
                let errorCount = 0;

                // Only process items that have status 'new' or 'update'
                const itemsToProcess = values.filter(game => game.status === 'new' || game.status === 'update');

                for (const game of itemsToProcess) {
                    try {
                        // Map filter names to ObjectIds
                        let filters = [];
                        if (game.filters_name && game.filters_name.trim()) {
                            const filterNames = game.filters_name.split(',').map(name => name.trim());
                            filters = filterNames.map(name => {
                                const filter = availableFilters.find(f => 
                                    f.name?.en === name || f.name?.zh === name || f.name === name
                                );
                                if (!filter && name) {
                                    console.warn(`Filter "${name}" not found in available filters`);
                                }
                                return filter?._id;
                            }).filter(Boolean);
                        }

                        // Map subject names to ObjectIds
                        let subjects = [];
                        if (game.subjects_name && game.subjects_name.trim()) {
                            const subjectNames = game.subjects_name.split(',').map(name => name.trim());
                            subjects = subjectNames.map(name => {
                                const subject = availableSubjects.find(s => 
                                    s.name?.en === name || s.name?.zh === name || s.name === name
                                );
                                if (!subject && name) {
                                    console.warn(`Subject "${name}" not found in available subjects`);
                                }
                                return subject?._id;
                            }).filter(Boolean);
                        }

                        // Check if this is an update (has ID) or create (no ID)
                        if (game.id && game.status === 'update') {
                            // Update existing game
                            const gameData = {
                                name: game.name,
                                description: game.description || '',
                                gameType: game.gameType,
                                order: game.order,
                                featureStart: formatDateForAPI(game.featureStart),
                                featureEnd: formatDateForAPI(game.featureEnd),
                                pointCorrect: game.pointCorrect || 0,
                                pointIncorrect: game.pointIncorrect || 0,
                                pointExchangeRate: game.pointExchangeRate || 0,
                                isActive: game.isActive !== false,
                                filters: filters,
                                subjects: subjects
                            };

                            await api.updateGame(game.id, gameData);
                        } else if (game.status === 'new') {
                            // Create new game
                            const gameData = {
                                name: game.name,
                                description: game.description || '',
                                gameType: game.gameType,
                                order: game.order,
                                featureStart: formatDateForAPI(game.featureStart),
                                featureEnd: formatDateForAPI(game.featureEnd),
                                pointCorrect: game.pointCorrect || 0,
                                pointIncorrect: game.pointIncorrect || 0,
                                pointExchangeRate: game.pointExchangeRate || 0,
                                isActive: game.isActive !== false,
                                filters: filters,
                                subjects: subjects
                            };

                            await api.createGame(gameData);
                        }

                        successCount++;
                    } catch (error) {
                        console.error(`Error processing game "${game.name}":`, error);
                        errorCount++;
                    }
                }

                if (errorCount === 0) {
                    nav('/admin/games');
                    snack.open(t('upload_success'));
                } else {
                    snack.open(t('import_completed', { success: successCount, errors: errorCount }));
                }
            } catch (error) {
                console.error('Upload error:', error);
                snack.error(t('upload_fail'));
            } finally {
                backDrop.close();
                helper.setSubmitting(false);
            }
        }
    });

    // Trigger validation whenever newGameList changes (like after CSV import)
    useEffect(() => {
        if (newGameList.length > 0) {
            // Small delay to ensure formik has been updated with new values
            const timer = setTimeout(() => {
                formik.validateForm().then(() => {
                    console.log('Validation completed after data import');
                });
            }, 100);
            return () => clearTimeout(timer);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newGameList]);

    const handleExport = () => {
        utils.exportCsv([], [
            { label: 'Order', value: row => row.order },
            { label: 'ID', value: row => row.id },
            { label: 'Name', value: row => row.name },
            { label: 'Description', value: row => row.description },
            { label: 'Game_Type', value: row => row.gameType },
            { label: 'Feature_Start', value: row => formatDateForDisplay(row.featureStart) },
            { label: 'Feature_End', value: row => formatDateForDisplay(row.featureEnd) },
            { label: 'Point_Correct', value: row => row.pointCorrect },
            { label: 'Point_Incorrect', value: row => row.pointIncorrect },
            { label: 'Point_Exchange_Rate', value: row => row.pointExchangeRate },
            { label: 'Is_Active', value: row => row.isActive },
            { label: 'Filters', value: row => row.filters_name },
            { label: 'Subjects', value: row => row.subjects_name },
        ], 'games_template');
    };

    const handleExportCurrentGames = () => {
        // Export all existing games with current data, sorted by order
        const sortedGames = _.orderBy(existingGames, ['order'], ['asc']);
        const currentGamesData = sortedGames.map(game => ({
            id: game._id || game.id,
            name: game.name || '',
            description: game.description || '',
            gameType: game.gameType || 'mc',
            order: game.order || 0,
            featureStart: formatDateForDisplay(game.featureStart),
            featureEnd: formatDateForDisplay(game.featureEnd),
            pointCorrect: game.pointCorrect || 0,
            pointIncorrect: game.pointIncorrect || 0,
            pointExchangeRate: game.pointExchangeRate || 0,
            isActive: game.isActive ? 'Active' : 'Inactive',
            filters_name: game.filters?.map(f => 
                f.name?.en || f.name?.zh || f.value || f._id
            ).join(', ') || '',
            subjects_name: game.subjects?.map(s => 
                s.name?.en || s.name?.zh || s.name || s._id
            ).join(', ') || ''
        }));

        utils.exportCsv(currentGamesData, [
            { label: 'Order', value: row => row.order },
            { label: 'ID', value: row => row.id },
            { label: 'Name', value: row => row.name },
            { label: 'Description', value: row => row.description },
            { label: 'Game_Type', value: row => row.gameType },
            { label: 'Feature_Start', value: row => row.featureStart },
            { label: 'Feature_End', value: row => row.featureEnd },
            { label: 'Point_Correct', value: row => row.pointCorrect },
            { label: 'Point_Incorrect', value: row => row.pointIncorrect },
            { label: 'Point_Exchange_Rate', value: row => row.pointExchangeRate },
            { label: 'Is_Active', value: row => row.isActive },
            { label: 'Filters', value: row => row.filters_name },
            { label: 'Subjects', value: row => row.subjects_name },
        ], 'games_data');
    };

    const handleCsv = async (f) => {
        setIsLoadingData(true);
        
        // If source data is still loading, store the file and wait
        if (isLoadingSourceData) {
            console.log('Source data still loading, storing CSV file for later processing...');
            setPendingCsvFile(f);
            setHasUploadedFile(true);
            return;
        }
        
        try {
            var csv = await utils.readFileAsText(f);
            var arr = await utils.csv2JsonArray(csv, {
            'Order': 'order',
            'ID': 'id',
            'Name': 'name',
            'Description': 'description',
            'Game_Type': 'gameType',
            'Feature_Start': 'featureStart',
            'Feature_End': 'featureEnd',
            'Point_Correct': 'pointCorrect',
            'Point_Incorrect': 'pointIncorrect',
            'Point_Exchange_Rate': 'pointExchangeRate',
            'Is_Active': 'isActive',
            'Filters_Name': 'filters_name',
            'Subjects_Name': 'subjects_name',
            // Support separate columns for filters and subjects
            'Filters': 'filters_name',
            'Subjects': 'subjects_name'
        });
        
        // Filter out completely empty rows (no name and no data)
        const validCsvData = _.filter(arr, item => 
            item.name || 
            item.id || 
            item.description || 
            item.gameType || 
            item.order !== undefined
        );
        
        
        // Compare with existing data and assign status
        const processedData = validCsvData.map(csvRow => {
            // Convert order to number but keep the original value for validation
            let orderValue = csvRow.order;
            if (orderValue !== undefined && orderValue !== null && orderValue !== '') {
                const parsedOrder = parseInt(orderValue);
                if (!isNaN(parsedOrder)) {
                    orderValue = parsedOrder;
                } else {
                    orderValue = csvRow.order;
                }
            } else {
                orderValue = 0;
            }

            // Check if this game already exists
            // Only match by ID if ID is provided in CSV - this determines if it's new or update
            const existing = csvRow.id && csvRow.id.trim() 
                ? _.find(existingGames, game => game._id === csvRow.id || game.id === csvRow.id)
                : null; // If no ID provided, treat as new game

            // Handle date fields - format them consistently, default to empty
            let featureStartValue = '';
            let featureEndValue = '';
            let hasInvalidDate = false;
            
            if (csvRow.featureStart !== undefined && csvRow.featureStart !== null && csvRow.featureStart !== '') {
                const formattedDate = formatDateForDisplay(csvRow.featureStart);
                featureStartValue = formattedDate;
                // Check if date is in valid format after formatting
                if (formattedDate && !/^\d{4}-\d{2}-\d{2}$/.test(formattedDate)) {
                    hasInvalidDate = true;
                }
            }
            
            if (csvRow.featureEnd !== undefined && csvRow.featureEnd !== null && csvRow.featureEnd !== '') {
                const formattedDate = formatDateForDisplay(csvRow.featureEnd);
                featureEndValue = formattedDate;
                // Check if date is in valid format after formatting
                if (formattedDate && !/^\d{4}-\d{2}-\d{2}$/.test(formattedDate)) {
                    hasInvalidDate = true;
                }
            }

            // Handle isActive - default to false (inactive) if not provided
            let isActiveValue = false; // Default to inactive
            let hasInvalidStatus = false;
            
            if (csvRow.isActive !== undefined && csvRow.isActive !== null && csvRow.isActive !== '') {
                const rawValue = csvRow.isActive;
                
                // Check for exact boolean values first (before string conversion)
                if (rawValue === true) {
                    isActiveValue = true;
                } else if (rawValue === false) {
                    isActiveValue = false;
                } else if (typeof rawValue === 'string') {
                    // Only accept exact valid string values (case-insensitive)
                    const val = rawValue.trim().toLowerCase();
                    if (val === 'active' || val === 'true') {
                        isActiveValue = true;
                    } else if (val === 'inactive' || val === 'false') {
                        isActiveValue = false;
                    } else {
                        // Invalid format - keep as the original string for validation error
                        isActiveValue = rawValue;
                        hasInvalidStatus = true;
                    }
                } else {
                    // Invalid type (number, object, etc.) - keep original for validation error
                    isActiveValue = rawValue;
                    hasInvalidStatus = true;
                }
            }

            // Handle required points fields
            let pointCorrectValue = (csvRow.pointCorrect !== undefined && csvRow.pointCorrect !== null && csvRow.pointCorrect !== '') ? parseInt(csvRow.pointCorrect) : undefined;
            let pointIncorrectValue = (csvRow.pointIncorrect !== undefined && csvRow.pointIncorrect !== null && csvRow.pointIncorrect !== '') ? parseInt(csvRow.pointIncorrect) : undefined;
            let pointExchangeRateValue = (csvRow.pointExchangeRate !== undefined && csvRow.pointExchangeRate !== null && csvRow.pointExchangeRate !== '') ? parseInt(csvRow.pointExchangeRate) : undefined;

            // Handle filters_name - check if value is explicitly provided in CSV (even if empty)
            let filtersNameValue = '';
            const hasFilterInCsv = csvRow.hasOwnProperty('filters_name') && csvRow.filters_name !== null && csvRow.filters_name !== undefined;
            
            if (hasFilterInCsv) {
                // Value is explicitly provided in CSV (could be empty string)
                filtersNameValue = csvRow.filters_name.toString().trim();
            } else if (existing) {
                // No value in CSV, use existing value
                filtersNameValue = existing.filters?.map(f => f.name?.en || f.name?.zh || f.value || f._id).join(', ') || '';
            }

            // Handle subjects_name - check if value is explicitly provided in CSV (even if empty)
            let subjectsNameValue = '';
            const hasSubjectInCsv = csvRow.hasOwnProperty('subjects_name') && csvRow.subjects_name !== null && csvRow.subjects_name !== undefined;
            
            if (hasSubjectInCsv) {
                // Value is explicitly provided in CSV (could be empty string)
                subjectsNameValue = csvRow.subjects_name.toString().trim();
            } else if (existing) {
                // No value in CSV, use existing value
                subjectsNameValue = existing.subjects?.map(s => s.name?.en || s.name?.zh || s.name || s._id).join(', ') || '';
            }

            let status = 'new';
            if (existing) {
                // Check if any fields have changed (update)
                const existingFiltersName = existing.filters?.map(f => 
                    f.name?.en || f.name?.zh || f.value || f._id
                ).join(', ') || '';
                const existingSubjectsName = existing.subjects?.map(s => 
                    s.name?.en || s.name?.zh || s.name || s._id
                ).join(', ') || '';
                
               
                
                // Normalize filters and subjects for comparison
                const normalizedExistingFilters = normalizeStr(existingFiltersName || '');
                const normalizedCsvFilters = normalizeStr(filtersNameValue || '');
                const normalizedExistingSubjects = normalizeStr(existingSubjectsName || '');
                const normalizedCsvSubjects = normalizeStr(subjectsNameValue || '');
                
                const hasChanges = 
                    normalizeStr(existing.name) !== normalizeStr(csvRow.name) ||
                    normalizeStr(existing.description || '') !== normalizeStr(csvRow.description || '') ||
                    normalizeStr(existing.gameType) !== normalizeStr(csvRow.gameType) ||
                    (existing.order || 0) !== (typeof orderValue === 'number' ? orderValue : parseInt(orderValue) || 0) ||
                    normalizeStr(formatDateForDisplay(existing.featureStart)) !== normalizeStr(featureStartValue) ||
                    normalizeStr(formatDateForDisplay(existing.featureEnd)) !== normalizeStr(featureEndValue) ||
                    (existing.pointCorrect || 0) !== (parseInt(csvRow.pointCorrect) || 0) ||
                    (existing.pointIncorrect || 0) !== (parseInt(csvRow.pointIncorrect) || 0) ||
                    (existing.pointExchangeRate || 0) !== (parseInt(csvRow.pointExchangeRate) || 0) ||
                    existing.isActive !== isActiveValue ||
                    normalizedExistingFilters !== normalizedCsvFilters ||
                    normalizedExistingSubjects !== normalizedCsvSubjects;
                
                status = hasChanges ? 'update' : 'existing';
            }
            return {
                ...csvRow,
                order: orderValue,
                featureStart: featureStartValue,
                featureEnd: featureEndValue,
                isActive: isActiveValue,
                filters_name: filtersNameValue,
                subjects_name: subjectsNameValue,
                pointCorrect: pointCorrectValue,
                pointIncorrect: pointIncorrectValue,
                pointExchangeRate: pointExchangeRateValue,
                status: (hasInvalidStatus || hasInvalidDate) ? 'error' : status // Mark as error if status or date format is invalid
            };
        });
        
        // Combine existing data with new imported data
        const existingNotInCsv = existingGames
            .filter(existingGame => {
                return !validCsvData.find(csvRow => 
                    (csvRow.id && (existingGame._id === csvRow.id || existingGame.id === csvRow.id)) ||
                    existingGame.name === csvRow.name
                );
            })
            .map(game => ({
                id: game._id || game.id,
                name: game.name || '',
                description: game.description || '',
                gameType: game.gameType || 'mc',
                order: game.order || 0,
                featureStart: formatDateForDisplay(game.featureStart),
                featureEnd: formatDateForDisplay(game.featureEnd),
                pointCorrect: game.pointCorrect || 0,
                pointIncorrect: game.pointIncorrect || 0,
                pointExchangeRate: game.pointExchangeRate || 0,
                isActive: game.isActive !== false,
                filters_name: game.filters?.map(f => 
                    f.name?.en || f.name?.zh || f.value || f._id
                ).join(', ') || '',
                subjects_name: game.subjects?.map(s => 
                    s.name?.en || s.name?.zh || s.name || s._id
                ).join(', ') || '',
                status: 'existing'
            }));
        
        const combinedData = [...existingNotInCsv, ...processedData];
        const filteredData = combinedData.filter(item => 
            item.status === 'new' || item.status === 'update' || item.status === 'error'
        );
        
        if (processedData.length > 0 && filteredData.length === 0) {
            snack.error(t('no_changes_detected') || 'No changes detected in the uploaded file.');
        }

        setNewGameList(filteredData);
        setHasUploadedFile(true);
        } catch (error) {
            console.error('Error processing CSV:', error);
        } finally {
            setIsLoadingData(false);
        }
    };

    const count = useMemo(() => {
        const statusCount = _.countBy(newGameList, r => r.status || 'existing');
        
        // Count validation errors
        const errorCount = _.size(formik.errors);
        if (errorCount > 0) {
            statusCount.error = errorCount;
        }
        
        return statusCount;
    }, [newGameList, formik.errors]);

    // Check if there are any changes (new or update items)
    const hasChanges = useMemo(() => {
        return (count.new || 0) > 0 || (count.update || 0) > 0;
    }, [count]);

    const handleConfirm = async () => {
        var errors = await formik.validateForm();
        if (_.size(errors) <= 0) {
            setOpen(true);
        }
    };

    const [open, setOpen] = useState();

    return (
        <LayoutContainer title={t('import_games')} backSrc="/admin/games">
            <Grid container spacing={1} mb={2}>
                <Grid item xs className='flex ac' sx={{ minWidth: 'min(100%,300px)', width: '200px' }}>
                    <Grid container spacing={1} >
                        <Grid item className='flex ac'>
                            <Box height='20px' width='20px' sx={{ backgroundColor: 'rgba(50, 255, 50,0.2) !important', mr: 1, border: '2px black solid' }} />
                            <span > {t('add_game_resource')} ({count.new || 0})</span>
                        </Grid>
                        <Grid item className='flex ac'>
                            <Box height='20px' width='20px' sx={{ backgroundColor: 'rgba(255, 255, 50,0.2)!important', mr: 1, border: '2px black solid' }} />
                            <span > {t('update_game')} ({count.update || 0})</span>
                        </Grid>
                        <Grid item className='flex ac'>
                            <Box height='20px' width='20px' sx={{ backgroundColor: 'rgba(255, 50, 50,0.2) !important', mr: 1, border: '2px black solid' }} />
                            <span > {t('invalid_data')} ({count.error || 0})</span>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item >
                    <Grid container spacing={1} >
                        <Grid item className='flex ac' >
                            <Button onClick={handleExport} variant='outlined'>
                                {t('download_template')}
                            </Button>
                        </Grid>
                        <Grid item className='flex ac' >
                            <Button 
                                onClick={handleExportCurrentGames} 
                                disabled={existingGames.length === 0}
                                variant='outlined'
                                startIcon={isLoadingSourceData ? <CircularProgress size={16} /> : null}
                            >
                                {t('download_current_games')}
                            </Button>
                        </Grid>
                        <Grid item className='flex ac' >
                            <Button 
                                onClick={() => utils.readFile(handleCsv, '.csv')}
                            >
                                {t('upload_update_file')}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            {sourceDataError && (
                <Box mb={2} p={2} sx={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: 1 }}>
                    <Typography variant="body1">
                        <strong>{t('error')}:</strong> {sourceDataError}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                        {t('please_refresh_and_try_again') || 'Please refresh the page and try again.'}
                    </Typography>
                </Box>
            )}
            <Box mb={2}>
                <Typography variant="h6" gutterBottom>
                    {t('game_upload_guide_title')}
                </Typography>

                <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                    {t('game_upload_step1_title')}
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    {t('game_upload_step1_desc')}
                </Typography>

                <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                    {t('game_upload_step2_title')}
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    {t('game_upload_step2_desc')}
                </Typography>

                <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                    {t('game_upload_step3_title')}
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    {t('game_upload_step3_desc')}
                </Typography>

                <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                    {t('game_upload_step4_title')}
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    {t('game_upload_step4_desc')}
                </Typography>

                <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
                    {t('game_upload_step5_title')}
                </Typography>
                <Typography variant="body2" component="div" sx={{ mb: 1 }}>
                    {t('game_upload_step5_desc')}
                </Typography>

                <Typography variant="subtitle2" component="div" sx={{ mt: 1, mb: 0.5 }}>
                    {t('game_upload_note_title')}
                </Typography>
                <Typography variant="body2" component="div" sx={{ color: '#666' }}>
                    • {t('game_upload_note1')}
                    <br />• {t('game_upload_note2')}
                </Typography>
            </Box>
            <GameUploadTable formik={formik} rows={newGameList} existingGames={existingGames} _orderBy='order' _order='asc' loading={isLoadingData || (pendingCsvFile !== null)} />
            {hasUploadedFile && (
                <Box display='flex' justifyContent='flex-end' mt={2}>
                    <Button onClick={handleConfirm} disabled={_.size(formik.errors) > 0 || !hasChanges}>
                        {t('save')}
                    </Button>
                </Box>
            )}
            <EditDialog title={t('confirm_import_games')} maxWidth='xs' confirmDisabled={formik.isSubmitting} handleSave={formik.handleSubmit} open={open} handleClose={() => setOpen(false)} confirmText={t('confirm')}>
                {count?.new > 0 && <p>{t('add_a_games', { a: count?.new })}</p>}
                {count?.update > 0 && <p>{t('update_a_games', { a: count?.update })}</p>}
            </EditDialog>
        </LayoutContainer>
    );
};

export default GameUpload;
